import { z } from 'zod';

import { passwordSchema } from '../auth/schemas';
import { ru } from '../i18n/ru';

/** Те же правила, что и на сервере: буквы, дефис, апостроф. */
const namePartRegex = /^[\p{L}][\p{L}\-' ]*$/u;

const phoneRegex = /^\+?[\d\s\-()]{10,20}$/;

const namePart = z
  .string()
  .min(1, ru.validation.required)
  .max(100, ru.validation.maxLength)
  .regex(namePartRegex, ru.validation.nameChars);

const optionalNamePart = z
  .string()
  .max(100, ru.validation.maxLength)
  .regex(namePartRegex, ru.validation.nameChars)
  .or(z.literal(''));

/**
 * Дата приходит из <input type="date"> строкой YYYY-MM-DD. Проверяем и формат,
 * и осмысленность: будущая дата рождения — всегда опечатка.
 */
const birthDate = z
  .string()
  .min(1, ru.validation.required)
  .refine((value) => !Number.isNaN(Date.parse(value)), ru.validation.date)
  .refine((value) => new Date(value) <= new Date(), ru.validation.birthDateFuture);

const optionalHttpUrl = z
  .string()
  .max(500, ru.validation.maxLength)
  .refine((value) => value === '' || /^https?:\/\//i.test(value), ru.validation.chatLink)
  .or(z.literal(''));

/** Сумма вводится строкой: пустое поле должно означать «не задано», а не 0. */
const optionalMoney = z
  .string()
  .refine((value) => value === '' || /^\d+([.,]\d{1,2})?$/.test(value), ru.validation.money)
  .or(z.literal(''));

export const groupSchema = z.object({
  name: z.string().min(1, ru.validation.required).max(100, ru.validation.maxLength),
  description: z.string().max(1000, ru.validation.maxLength).or(z.literal('')),
  monthlyPrice: z
    .string()
    .min(1, ru.validation.required)
    .regex(/^\d+([.,]\d{1,2})?$/, ru.validation.money),
  telegramUrl: optionalHttpUrl,
  whatsAppUrl: optionalHttpUrl,
  maxUrl: optionalHttpUrl,
  // Идентификатор чата из списка известных боту; пусто — не рассылать.
  telegramChatId: z.string(),
  isActive: z.boolean(),
});

export const studentSchema = z.object({
  lastName: namePart,
  firstName: namePart,
  middleName: optionalNamePart,
  birthDate,
  groupId: z.string().min(1, ru.validation.groupRequired),
});

export const adminStudentSchema = studentSchema.extend({
  // Будущая дата допустима: ученика записывают заранее, «начнёт с сентября».
  classesStartDate: z
    .string()
    .min(1, ru.validation.required)
    .refine((value) => !Number.isNaN(Date.parse(value)), ru.validation.date),
  parentUserId: z.string().min(1, ru.validation.parentRequired),
  monthlyPriceOverride: optionalMoney,
  isActive: z.boolean(),
});

/**
 * Месяц едет одной строкой «2026-08»: Select работает со строками, а разбирать
 * пару чисел из двух полей формы значило бы держать их в согласии вручную.
 */
export const subscriptionSchema = z.object({
  period: z.string().min(1, ru.validation.monthRequired),
  comment: z.string().max(1000, ru.validation.maxLength).or(z.literal('')),
  personalDataConsent: z.boolean().refine((value) => value, ru.validation.consentRequired),
  termsAccepted: z.boolean().refine((value) => value, ru.validation.termsRequired),
});

/**
 * Реквизиты для квитанции. Правила те же, что на сервере: подсказка должна
 * появиться до отправки формы, но последнее слово за сервером.
 */
export const requisitesSchema = z.object({
  businessName: z.string().min(1, ru.validation.required).max(200, ru.validation.maxLength),
  entrepreneurFullName: z.string().min(1, ru.validation.required).max(200, ru.validation.maxLength),
  inn: z.string().regex(/^(\d{10}|\d{12})$/, ru.requisites.innRule),
  taxSystem: z.string().min(1, ru.validation.required).max(120, ru.validation.maxLength),
  sellerPosition: z.string().min(1, ru.validation.required).max(100, ru.validation.maxLength),
  sellerFullName: z.string().min(1, ru.validation.required).max(200, ru.validation.maxLength),
});

/**
 * Учётная запись глазами администратора. Email правится: заводить человека
 * заново из-за опечатки в адресе значило бы потерять его учеников и оплаты.
 *
 * Пароль задаётся только при заведении — администратор передаёт его лично.
 * В правке поле скрыто, и правило к нему не применяется: пустая строка там
 * означает «пароль не трогаем», а не «пароль слишком короткий».
 */
export const userFormSchema = (withPassword: boolean) =>
  z.object({
    email: z.email(ru.validation.email).max(256, ru.validation.maxLength),
    password: withPassword ? passwordSchema : z.string(),
    lastName: namePart,
    firstName: namePart,
    middleName: optionalNamePart,
    phone: z.string().regex(phoneRegex, ru.validation.phone).or(z.literal('')),
  });

export const periodValue = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, '0')}`;

export const parsePeriod = (value: string): { year: number; month: number } => {
  const [year, month] = value.split('-');
  return { year: Number(year), month: Number(month) };
};

export type GroupValues = z.infer<typeof groupSchema>;
export type StudentValues = z.infer<typeof studentSchema>;
export type AdminStudentValues = z.infer<typeof adminStudentSchema>;
export type SubscriptionValues = z.infer<typeof subscriptionSchema>;
export type RequisitesValues = z.infer<typeof requisitesSchema>;
export type UserValues = z.infer<ReturnType<typeof userFormSchema>>;

/** Запятая как разделитель привычнее, но на сервер уходит число. */
export const toNumber = (value: string): number => Number(value.replace(',', '.'));

export const toOptionalNumber = (value: string): number | null =>
  value.trim() === '' ? null : toNumber(value);

export const blankToNull = (value: string): string | null =>
  value.trim() === '' ? null : value.trim();
